import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { getStageConfig } from "@/data/workflowEngine";
import { useAppData } from "@/data/AppDataContext";
import type { CloseoutStatus } from "@/data/types";
import { CloseoutService } from "@/services/closeout.service";
import type { CloseoutQueueItem } from "@/services/closeout.service";
import { toast } from "sonner";
import { StatusBadge } from "@/components/StatusBadge";
import { Progress } from "@/components/ui/progress";
import { QADetailDialog } from "./ProjectCloseout";
import {
  CockpitPage,
  CockpitMasthead,
  HeroMetric,
  HeroDivider,
  CockpitFilterBar,
  CockpitSearch,
  CockpitChipGroup,
  CockpitListShell,
  CockpitColumns,
  CockpitRow,
  CockpitEmpty,
  InlineMeta,
  StatusDot,
} from "@/components/cockpit/CockpitShell";

const CLOSEOUT_STAGES: CloseoutStatus[] = ["Not Started", "Install Complete", "QA Passed", "Final Photos Complete", "Issues Resolved", "Ready for Closeout", "Closed"];
const TEMPLATE = "minmax(170px,1fr) minmax(180px,1.1fr) 130px 110px 90px 90px 24px";

function getJobDisplayRef(job: { id: string; jobNumber?: unknown }): string {
  const jobNumber = typeof job.jobNumber === "string" ? job.jobNumber.trim() : "";
  return jobNumber || job.id;
}

function getInstallerDisplayName(job: { assignedCrew?: string; assignedSubcontractor?: string }): string {
  return job.assignedCrew || job.assignedSubcontractor || "Unassigned";
}

export default function ProjectCloseoutCockpit() {
  const [searchParams] = useSearchParams();
  const { jobs: appJobs, issues, credits } = useAppData();
  const [queueItems, setQueueItems] = useState<CloseoutQueueItem[]>([]);
  const queueRequestInFlightRef = useRef(false);
  const queueBootstrappedRef = useRef(false);
  const [loadingQueue, setLoadingQueue] = useState(true);
  const [search, setSearch] = useState("");
  const [filterStage, setFilterStage] = useState<CloseoutStatus | "All">("All");
  const [detailJobId, setDetailJobId] = useState<string | null>(null);
  const queueJobs = useMemo(() => queueItems.map((item) => item.job), [queueItems]);
  const jobs = queueJobs.length > 0 ? queueJobs : appJobs;

  const loadQueue = useCallback(async () => {
    if (queueRequestInFlightRef.current) return;
    queueRequestInFlightRef.current = true;
    setLoadingQueue(true);
    const { data, error } = await CloseoutService.getQueue();
    if (error) {
      toast.error("Failed to load closeout queue");
    } else {
      setQueueItems(data ?? []);
    }
    setLoadingQueue(false);
    queueRequestInFlightRef.current = false;
  }, []);

  useEffect(() => {
    if (queueBootstrappedRef.current) return;
    queueBootstrappedRef.current = true;
    void loadQueue();
  }, [loadQueue]);

  const relevantJobs = useMemo(() => jobs.filter((j) => j.closeoutStatus !== "Not Started" || (getStageConfig(j.currentStage)?.order ?? 0) >= 90), [jobs]);
  const filtered = useMemo(() => relevantJobs.filter((j) => {
    const matchSearch =
      j.customerName.toLowerCase().includes(search.toLowerCase()) ||
      j.id.toLowerCase().includes(search.toLowerCase()) ||
      getJobDisplayRef(j).toLowerCase().includes(search.toLowerCase());
    const matchStage = filterStage === "All" || j.closeoutStatus === filterStage;
    return matchSearch && matchStage;
  }), [relevantJobs, search, filterStage]);
  const pagination = usePagination(filtered, 20);
  useEffect(() => {
    pagination.reset();
  }, [search, filterStage]);
  const stageCounts = CLOSEOUT_STAGES.map((stage) => ({ stage, count: relevantJobs.filter((j) => j.closeoutStatus === stage).length }));

  const awaitingCloseout = relevantJobs.filter((j) => j.closeoutStatus === "Ready for Closeout");
  const qaReview = relevantJobs.filter((j) => (getStageConfig(j.currentStage)?.order ?? 0) === 90 && j.closeoutStatus !== "QA Passed" && j.closeoutStatus !== "Closed");
  const incompleteDocs = relevantJobs.filter((j) => !j.finalDocumentationComplete && j.closeoutStatus !== "Not Started" && j.closeoutStatus !== "Closed");
  const closed = relevantJobs.filter((j) => j.closeoutStatus === "Closed");
  const detailJob = detailJobId ? jobs.find((j) => j.id === detailJobId) : null;

  useEffect(() => {
    const targetJobId = searchParams.get("job");
    if (!targetJobId) return;
    if (!jobs.some((j) => j.id === targetJobId)) return;
    setDetailJobId((prev) => prev ?? targetJobId);
  }, [searchParams, jobs]);

  return (
    <CockpitPage>
      <CockpitMasthead
        // eyebrow="Post-install · QA gate"
        title="QA / Closeout"
        right={(
          <>
            <HeroMetric label="QA review" value={qaReview.length} hero tone={qaReview.length > 0 ? "warn" : "default"} />
            <HeroDivider />
            <HeroMetric label="Ready" value={awaitingCloseout.length} tone="good" />
            <HeroMetric label="Docs missing" value={incompleteDocs.length} tone={incompleteDocs.length > 0 ? "bad" : "default"} />
            <HeroMetric label="Closed" value={closed.length} />
          </>
        )}
      />

      <CockpitFilterBar
        search={<CockpitSearch value={search} onChange={setSearch} placeholder="Search jobs or customers" />}
        chips={<CockpitChipGroup value={filterStage} onChange={(v) => setFilterStage(v as CloseoutStatus | "All")} options={[{ value: "All", label: "All", count: relevantJobs.length }, ...stageCounts.filter((s) => s.count > 0).map((s) => ({ value: s.stage, label: s.stage, count: s.count }))]} />}
      />

      <CockpitListShell>
        {loadingQueue && <div className="px-2 py-1 text-[11px] text-muted-foreground">Loading closeout queue...</div>}
        <CockpitColumns template={TEMPLATE}>
          <span>Job</span><span>Customer</span><span>Closeout</span><span>Progress</span><span>Issues</span><span>Credits</span><span />
        </CockpitColumns>
        {filtered.length === 0 ? <CockpitEmpty message="No jobs match this closeout view." /> : pagination.paged.map((job) => {
          const openIssues = issues.filter((i) => i.jobId === job.id && (i.status === "Open" || i.status === "In Progress"));
          const jobCredits = credits.filter((c) => c.jobId === job.id);
          const creditTotal = jobCredits.reduce((s, c) => s + c.creditAmount, 0);
          const stageIndex = Math.max(0, CLOSEOUT_STAGES.indexOf(job.closeoutStatus));
          const progress = Math.round((stageIndex / (CLOSEOUT_STAGES.length - 1)) * 100);
          return (
            <CockpitRow key={job.id} template={TEMPLATE} onClick={() => setDetailJobId(job.id)}>
              <div><div className="flex items-center gap-2"><StatusDot tone={job.closeoutStatus === "Closed" ? "good" : openIssues.length > 0 ? "bad" : "default"} /><p className="text-sm font-medium text-foreground tabular-nums">{getJobDisplayRef(job)}</p></div><InlineMeta items={[job.projectType, getInstallerDisplayName(job)]} /></div>
              <p className="text-sm text-foreground truncate">{job.customerName}</p>
              <StatusBadge type="closeout" value={job.closeoutStatus} />
              <div className="space-y-1"><p className="text-xs text-muted-foreground tabular-nums">{progress}%</p><Progress value={progress} className="h-1.5" /></div>
              <p className={openIssues.length > 0 ? "text-sm text-destructive tabular-nums" : "text-sm text-muted-foreground tabular-nums"}>{openIssues.length || "—"}</p>
              <p className="text-sm text-muted-foreground tabular-nums">{creditTotal ? `$${creditTotal.toLocaleString()}` : "—"}</p>
            </CockpitRow>
          );
        })}
      </CockpitListShell>
      <PaginationBar {...pagination} />

      {detailJob && (
        <QADetailDialog
          job={detailJob}
          open={!!detailJobId}
          onOpenChange={(o) => !o && setDetailJobId(null)}
          onMutated={loadQueue}
        />
      )}
    </CockpitPage>
  );
}
